import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
};

// Company APIs
export const companyAPI = {
  create: (data) => api.post('/api/companies/', data),
  getMine: () => api.get('/api/companies/me'),
  list: () => api.get('/api/companies/'),
};

// Inventory APIs
export const inventoryAPI = {
  create: (data) => api.post('/api/inventory/', data),
  list: (params) => api.get('/api/inventory/', { params }),
  getMyItems: () => api.get('/api/inventory/my-items'),
  getItem: (id) => api.get(`/api/inventory/${id}`),
  update: (id, data) => api.put(`/api/inventory/${id}`, data),
  delete: (id) => api.delete(`/api/inventory/${id}`),
  getCategories: () => api.get('/api/inventory/categories'),
};

// Transaction / Order APIs
export const transactionAPI = {
  create: (data) => api.post('/api/transactions/', data),
  myPurchases: () => api.get('/api/transactions/my-purchases'),
  mySales: () => api.get('/api/transactions/my-sales'),
  get: (id) => api.get(`/api/transactions/${id}`),
  update: (id, data) => api.put(`/api/transactions/${id}`, data),
  cancel: (id) => api.put(`/api/transactions/${id}/cancel`),
};

// Wishlist APIs
export const wishlistAPI = {
  add: (itemId) => api.post(`/api/wishlist/${itemId}`),
  remove: (itemId) => api.delete(`/api/wishlist/${itemId}`),
  getAll: () => api.get('/api/wishlist/'),
  check: (itemId) => api.get(`/api/wishlist/check/${itemId}`),
};

// Cart APIs
export const cartAPI = {
  add: (data) => api.post('/api/cart/', data),
  getAll: () => api.get('/api/cart/'),
  remove: (itemId) => api.delete(`/api/cart/${itemId}`),
  clear: () => api.delete('/api/cart/clear'),
  count: () => api.get('/api/cart/count'),
};

// Review APIs
export const reviewAPI = {
  create: (data) => api.post('/api/reviews/', data),
  getSellerReviews: (sellerId) => api.get(`/api/reviews/seller/${sellerId}`),
};

// Notification APIs
export const notificationAPI = {
  getAll: () => api.get('/api/notifications/'),
  unreadCount: () => api.get('/api/notifications/unread-count'),
  markRead: (id) => api.put(`/api/notifications/${id}/read`),
  markAllRead: () => api.put('/api/notifications/read-all'),
};

// Message APIs
export const messageAPI = {
  send: (data) => api.post('/api/messages/', data),
  getConversations: () => api.get('/api/messages/conversations'),
  getConversation: (userId) => api.get(`/api/messages/with/${userId}`),
};

// Payment APIs
export const paymentAPI = {
  createOrder: (data) => api.post('/api/payments/create-order', data),
  verify: (data) => api.post('/api/payments/verify', data),
  getStatus: (transactionId) => api.get(`/api/payments/status/${transactionId}`),
};

// Image Upload APIs
export const imageAPI = {
  upload: (file, folder = 'items') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/images/upload?folder=${folder}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadMultiple: (files, folder = 'items') => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post(`/api/images/upload-multiple?folder=${folder}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadItemImages: (itemId, files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post(`/api/images/item/${itemId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Bulk Upload APIs
export const bulkUploadAPI = {
  uploadCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/bulk-upload/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getTemplate: () => api.get('/api/bulk-upload/template'),
};

// GST Verification APIs
export const gstAPI = {
  verify: (gstNumber) => api.post('/api/gst/verify', { gst_number: gstNumber }),
  verifyCompany: (gstNumber) => api.post('/api/gst/verify-company', { gst_number: gstNumber }),
  validateFormat: (gstNumber) => api.get(`/api/gst/validate/${gstNumber}`),
};

// KYC & Document APIs
export const kycAPI = {
  upload: (documentType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/kyc/upload?document_type=${documentType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getMyDocuments: () => api.get('/api/kyc/my-documents'),
  reviewDocument: (docId, status, notes) => api.put(`/api/kyc/review/${docId}?status=${status}&notes=${notes || ''}`),
};

// Dispute APIs
export const disputeAPI = {
  raise: (data) => api.post('/api/disputes/', data),
  getMyDisputes: () => api.get('/api/disputes/my-disputes'),
  getAll: () => api.get('/api/disputes/all'),
  resolve: (id, resolution, notes) => api.put(`/api/disputes/${id}/resolve?resolution=${resolution}&notes=${notes || ''}`),
};

// Logistics APIs
export const logisticsAPI = {
  estimate: (data) => api.post('/api/logistics/estimate', data),
  createShipment: (data) => api.post('/api/logistics/create-shipment', data),
  track: (awbNumber) => api.get(`/api/logistics/track/${awbNumber}`),
  getShipment: (transactionId) => api.get(`/api/logistics/shipment/${transactionId}`),
};

// Analytics APIs
export const analyticsAPI = {
  sellerDashboard: (days = 30) => api.get(`/api/analytics/seller-dashboard?days=${days}`),
  demandForecast: (category, days = 30) => api.get(`/api/analytics/demand-forecast/${category}?days=${days}`),
  marketInsights: () => api.get('/api/analytics/market-insights'),
  priceComparison: (itemId) => api.get(`/api/analytics/price-comparison/${itemId}`),
};

// AI Agent APIs
export const aiAPI = {
  analyze: (data) => api.post('/api/ai/analyze', data),
  matchBuyers: (itemId) => api.get(`/api/ai/match-buyers/${itemId}`),
  negotiate: (data) => api.post('/api/ai/negotiate', data),
  recommendations: (itemId) => api.get(`/api/ai/recommendations/${itemId}`),
  search: (data) => api.post('/api/ai/search', data),
  indexItem: (itemId) => api.post(`/api/ai/index-item/${itemId}`),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/api/admin/stats'),
  getUsers: (params) => api.get('/api/admin/users', { params }),
  getOrders: (params) => api.get('/api/admin/orders', { params }),
  verifyCompany: (id) => api.put(`/api/admin/companies/${id}/verify`),
  getPendingCompanies: () => api.get('/api/admin/companies/pending'),
};

// Chatbot APIs
export const chatbotAPI = {
  chat: (message, context) => api.post('/api/chatbot/chat', { message, context }),
  chatPublic: (message) => api.post('/api/chatbot/chat-public', { message }),
};

// WebSocket for real-time chat
export const createChatWebSocket = (userId) => {
  const wsUrl = API_BASE_URL.replace('http', 'ws');
  return new WebSocket(`${wsUrl}/ws/chat/${userId}`);
};

export default api;

// Users APIs
export const usersAPI = {
  getMe: () => api.get('/api/users/me'),
  enableSeller: () => api.post('/api/users/me/enable-seller'),
  disableSeller: () => api.post('/api/users/me/disable-seller'),
};
